<template>
  <div class="medical-dashboard doctor-dashboard container-fluid py-3">
    <!-- Doctor Shift Availability Modal -->
    <DoctorShiftModal ref="shiftModal" @session-started="onSessionStarted" @shift-updated="onSessionStarted"></DoctorShiftModal>

    <!-- Doctor Header Banner -->
    <div class="doctor-hero-card p-4 mb-3">
      <div class="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-3">
        <div>
          <div class="doctor-card-breadcrumb text-xs text-secondary font-weight-bold mb-1">
            Medical Dashboard · {{ resolvedHospitalName }}
          </div>
          <h3 class="doctor-card-title mb-1 font-weight-bolder text-dark">
            {{ doctorGreeting }}
          </h3>
          <p class="doctor-card-subtitle text-sm text-secondary mb-0">
            {{ doctorSubheader }}
          </p>
        </div>

        <div class="d-flex flex-wrap align-items-center gap-2">
          <!-- Status Segmented Control (Available, Busy, Break, Offline) -->
          <div class="doctor-status-segmented-control">
            <button
              v-for="opt in doctorHeaderStatusOptions"
              :key="opt.value"
              type="button"
              class="status-segment-btn"
              :class="{ active: isCurrentDoctorStatus(opt.value), [opt.activeClass]: isCurrentDoctorStatus(opt.value) }"
              @click="setDoctorHeaderStatus(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>

          <!-- Emergency Button -->
          <button
            type="button"
            class="btn emergency-header-btn mb-0 d-flex align-items-center gap-2"
            @click="scrollToEmergency"
            title="Go to emergency broadcast"
          >
            <span>Emergency</span>
            <span v-if="emergencyTokens.length" class="badge bg-danger text-white rounded-pill px-2">
              {{ emergencyTokens.length }}
            </span>
          </button>

          <!-- Audio / Voice Announcement Settings Button -->
          <button
            type="button"
            class="btn btn-outline-teal btn-sm mb-0 d-flex align-items-center gap-1 font-weight-bold"
            @click="openVoiceSettings"
            title="Audio & Chime Announcement Settings"
          >
            <i class="fas fa-volume-up"></i>
            <span>Audio</span>
          </button>
        </div>
      </div>

      <!-- Bottom Pill Badges Row: Shift, Slot, Counter/Room -->
      <div class="d-flex flex-wrap align-items-center gap-2 pt-1">
        <span class="doctor-info-pill" @click="openShiftModal" title="Click to update shift">
          Shift {{ currentDoctorShiftDisplay }}
        </span>
        <span class="doctor-info-pill" @click="openShiftModal" title="Click to update slot duration">
          {{ currentDoctorSlotDisplay }} min per patient
        </span>
        <span class="doctor-info-pill" @click="openShiftModal" title="Click to update counter/room">
          {{ currentDoctorRoomDisplay }}
        </span>
      </div>
    </div>

    <!-- Active emergency broadcast banner -->
    <div v-if="activeEmergency" class="alert alert-danger shadow-sm border-0 d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 text-white p-3 rounded-4 animate-pulse">
      <div class="d-flex align-items-center gap-3">
        <i class="fas fa-exclamation-triangle fa-2x text-white"></i>
        <div>
          <h6 class="mb-0 font-weight-bold text-white">Emergency broadcast is live: {{ activeEmergency.title }}</h6>
          <small class="text-white-75">{{ activeEmergency.description }}</small>
        </div>
      </div>
      <button class="btn btn-light btn-sm text-danger font-weight-bold mb-0" @click="dismissEmergency">
        Stop broadcast
      </button>
    </div>

    <!-- Doctor KPI Cards Row -->
    <div class="row g-3 mb-3">
      <div v-for="kpi in kpiCards" :key="kpi.key" class="col-6 col-md-4 col-xl">
        <div class="md-card kpi-card h-100 text-center p-3">
          <div class="kpi-icon mx-auto mb-2" :class="kpi.tone">
            <i :class="kpi.icon"></i>
          </div>
          <p class="kpi-label mb-1">{{ kpi.label }}</p>
          <h3 class="kpi-value mb-1">
            {{ kpi.value }}<span v-if="kpi.total !== undefined" class="kpi-total"> / {{ kpi.total }}</span>
          </h3>
          <small class="kpi-sub" :class="kpi.subClass">{{ kpi.sub }}</small>
        </div>
      </div>
    </div>

    <!-- Doctor Main Layout: Live Queue (Left) & Shift / Broadcast / Screens (Right) -->
    <div class="row g-3">
      <!-- Left Column: Live Queue -->
      <div class="col-12 col-lg-7 col-xl-7">
        <div class="md-card live-queue-card p-3 mb-3">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="mb-0 font-weight-bold text-dark">
              Live queue
              <span v-if="selectedCalendarDate && selectedCalendarDate !== todayDateIso" class="text-xs text-secondary ms-1 font-weight-normal">
                ({{ formattedSelectedCalendarDate }})
              </span>
            </h6>
            <div class="d-flex align-items-center gap-2">
              
              <span class="live-waiting-badge">
                {{ waitingTokensCount }} waiting
              </span>
            </div>
          </div>

          <!-- Now consulting box -->
          <div class="now-serving-box p-3 mb-3">
            <p class="now-serving-label mb-1">Now consulting</p>
            <h2 class="now-serving-token-number mb-1">
              {{ currentlyServingToken ? currentlyServingToken.token_number : '--' }}
            </h2>
            <p class="now-serving-patient-name mb-1">
              {{ currentlyServingToken ? formatPatientNameAndAge(currentlyServingToken) : 'No patient in consultation' }}
            </p>
            <p class="now-serving-subtext mb-2">
              <template v-if="currentlyServingToken">
                <template v-if="isCurrentTokenServing">
                  In room for {{ servingDurationDisplay }} · {{ currentlyServingService }}
                </template>
                <template v-else-if="isCurrentTokenCalling">
                  Calling patient · Click Consulting when in room · {{ currentlyServingService }}
                </template>
                <template v-else>
                  In room for {{ servingDurationDisplay }} · {{ currentlyServingService }}
                </template>
              </template>
              <template v-else>
                Room ready for next patient
              </template>
            </p>

            <!-- Progress bar -->
            <div class="now-serving-progress-track">
              <div
                class="now-serving-progress-bar"
                :style="{ width: currentlyServingToken ? `${servingProgressPct}%` : '0%' }"
              ></div>
            </div>

<!-- Action buttons -->
                  <div class="d-flex align-items-center flex-wrap gap-2 pt-1">
                    <!-- 1. Waiting -->
                    <button
                      type="button"
                      class="btn btn-serving-wait mb-0"
                      :disabled="!currentlyServingToken || isUpdatingQueue"
                      @click="moveTokenToWaiting"
                    >
                      <i class="fas fa-clock me-1"></i>Waiting
                    </button>

                    <!-- 2. Calling -->
                    <button
                      type="button"
                      class="btn btn-serving-call mb-0"
                      :class="{ 'btn-status-active': isCurrentTokenCalling }"
                      :disabled="!currentlyServingToken || isUpdatingQueue"
                      @click="setTokenCalling"
                    >
                      <i class="fas fa-bullhorn me-1"></i>Calling
                    </button>

                    <!-- 3. Consulting -->
                    <button
                      type="button"
                      class="btn btn-serving-serve mb-0"
                      :class="{ 'btn-status-active': isCurrentTokenServing }"
                      :disabled="!currentlyServingToken || isUpdatingQueue"
                      @click="setTokenServing"
                    >
                      <i class="fas fa-user-md me-1"></i>Consulting
                    </button>

                    <!-- 4. Skip -->
                    <button
                      type="button"
                      class="btn btn-serving-skip mb-0"
                      :disabled="!currentlyServingToken || isUpdatingQueue"
                      @click="skipServingToken"
                    >
                      <i class="fas fa-forward me-1"></i>Skip
                    </button>

                    <!-- 5. Complete -->
                    <button
                      type="button"
                      class="btn btn-serving-complete mb-0"
                      :disabled="!currentlyServingToken || isUpdatingQueue"
                      @click="completeServingToken"
                    >
                      <i class="fas fa-check-circle me-1"></i>Complete
                    </button>
                  </div>
          </div>

          <!-- Call Next Patient button -->
          <button
            type="button"
            class="btn call-next-patient-btn w-100 mb-3 d-flex align-items-center justify-content-center"
            :disabled="isCallingNext || (waitingTokensList.length === 0 && !hasEmergencyTokens)"
            @click="handleCallNextTrigger"
          >
            <span v-if="nextWaitingToken">
              Call next patient: {{ nextWaitingToken.token_number }} {{ nextWaitingToken.token_name || '' }}
            </span>
            <span v-else-if="hasEmergencyTokens">
              <i class="fas fa-ambulance me-2 text-danger"></i>Call Emergency Patient ({{ emergencyPendingTokens.length }} waiting)
            </span>
            <span v-else>
              No patients waiting
            </span>
          </button>

          <!-- Next in line section -->
          <div class="next-in-line-section">
            <h6 class="next-in-line-header mb-2">Next in line</h6>

            <div v-if="waitingTokensList.length > 0" class="next-in-line-list d-flex flex-column gap-2">
              <div
                v-for="tok in waitingTokensList"
                :key="tok.id"
                class="next-patient-item d-flex align-items-center justify-content-between py-2"
                :class="{ 'item-skipped-highlight': isSkippedToken(tok) }"
                style="cursor: pointer;"
                title="Click to call patient"
                @click="assignOrCallToken(tok)"
              >
                <div class="d-flex align-items-center gap-3">
                  <span class="patient-token-badge" :class="{ 'token-skipped-badge': isSkippedToken(tok) }">
                    {{ tok.token_number }}
                  </span>
                  <div>
                    <div class="patient-item-name d-flex align-items-center gap-2">
                      <span>{{ formatPatientNameAndAge(tok) }}</span>
                      <span v-if="isSkippedToken(tok)" class="skipped-pill-badge">
                        <i class="fas fa-forward me-1"></i>Skipped
                      </span>
                    </div>
                    <div class="patient-item-sub">
                      {{ tok.service?.name || tok.service_name || tok.counter?.name || tok.counter_name || 'General OPD' }} · waiting {{ getWaitingMinutes(tok) }} min
                      <small v-if="tok.description" class="d-block text-secondary text-truncate mt-1" style="max-width: 250px;" :title="tok.description">
                        <i class="fas fa-notes-medical me-1 text-teal"></i>{{ tok.description }}
                      </small>
                    </div>
                  </div>
                </div>
                <span :class="['patient-tag-badge', getPatientTagBadgeClass(tok)]">
                  {{ getPatientTagText(tok) }}
                </span>
              </div>
            </div>

            <div v-else class="text-center py-4 text-muted">
              <i class="fas fa-user-clock fa-2x mb-2 text-secondary opacity-5"></i>
              <p class="text-xs mb-0">No patients waiting in queue</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: My shift and room, Emergency broadcast, Display screens -->
      <div class="col-12 col-lg-5 col-xl-5">
        <!-- Doctor Interactive Monthly Calendar (Booked Tokens on Dates) -->
        <div class="md-card p-3 mb-3 doctor-calendar-card shadow-xs">
          <!-- Header with Month Year and Prev/Next chevrons -->
          <div class="d-flex align-items-center justify-content-between mb-3 px-1">
            <h6 class="mb-0 doctor-calendar-header-title">{{ calendarMonthYearHeader }}</h6>
            <div class="d-flex align-items-center gap-1">
              <button
                type="button"
                class="doctor-calendar-nav-btn shadow-none"
                @click="prevCalendarMonth"
                title="Previous Month"
              >
                <i class="fas fa-chevron-left"></i>
              </button>
              <button
                type="button"
                class="doctor-calendar-nav-btn shadow-none"
                @click="nextCalendarMonth"
                title="Next Month"
              >
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>

          <!-- Weekday names: Sun Mon Tue Wed Thu Fri Sat -->
          <div class="doctor-calendar-weekdays mb-2">
            <div v-for="w in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']" :key="w" class="doctor-calendar-weekday">
              {{ w }}
            </div>
          </div>

          <!-- Days grid with booked token indicators -->
          <div class="doctor-calendar-grid mb-2">
            <div
              v-for="(dayObj, idx) in calendarDays"
              :key="idx"
              class="doctor-calendar-day"
              :class="{
                'is-other-month': !dayObj.isCurrentMonth,
                'is-today': dayObj.isToday && !dayObj.isSelected,
                'is-selected': dayObj.isSelected,
                'has-tokens': dayObj.hasTokens
              }"
              @click="selectCalendarDate(dayObj)"
              :title="dayObj.hasTokens ? `${dayObj.tokenCount} patient(s) assigned` : ''"
            >
              <span class="day-num">{{ dayObj.day }}</span>
              <span
                v-if="dayObj.hasTokens"
                class="calendar-token-count-badge"
                :class="{ 'on-selected': dayObj.isSelected }"
                :title="`${dayObj.tokenCount} patient(s)`"
              >
                {{ dayObj.tokenCount }}
              </span>
            </div>
          </div>

          <!-- Footer with selected date and Today button -->
          <div class="d-flex align-items-center justify-content-between pt-2 mt-2 border-top">
            <span class="text-xs text-secondary font-weight-bold">
              <i class="fas fa-calendar-alt text-teal me-1"></i>{{ formattedSelectedCalendarDate }}
            </span>
            <button
              type="button"
              class="btn btn-xs btn-outline-dark mb-0 py-1 px-2 rounded-pill font-weight-bold"
              @click="resetCalendarToToday"
            >
              Today
            </button>
          </div>

          <!-- Booked Tokens Section for Selected Calendar Date -->
          <div class="doctor-calendar-booked-section pt-3 mt-2 border-top">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <div class="d-flex align-items-center gap-2">
                <i class="fas fa-ticket-alt text-teal"></i>
                <h6 class="text-xs font-weight-bolder text-dark mb-0">
                  Booked Tokens
                  <span class="badge bg-teal-soft text-teal ms-1 font-weight-bold">
                    {{ selectedDateTokens.length }}
                  </span>
                </h6>
              </div>
            </div>

            <!-- Loading state -->
            <div v-if="isFetchingCalendarTokens" class="text-center py-3 text-secondary">
              <span class="spinner-border spinner-border-sm text-teal me-1" role="status"></span>
              <span class="text-xs">Loading booked tokens...</span>
            </div>

            <!-- Booked tokens list -->
            <div v-else-if="selectedDateTokens.length" class="doctor-calendar-token-list d-flex flex-column gap-2" style="max-height: 180px; overflow-y: auto;">
              <div
                v-for="tok in selectedDateTokens"
                :key="tok.id"
                class="doctor-booked-token-item p-2 rounded-3 border d-flex align-items-center justify-content-between bg-white"
              >
                <div class="d-flex align-items-center gap-2">
                  <div class="booked-token-badge font-weight-bolder">
                    {{ tok.token_number || tok.token_no || `#${tok.id}` }}
                  </div>
                  <div>
                    <div class="text-xs font-weight-bold text-dark text-truncate" style="max-width: 150px;">
                      {{ formatPatientNameAndAge(tok) || tok.patient_name || tok.customer_name || 'Patient' }}
                    </div>
                    <div class="text-xxs text-secondary d-flex align-items-center gap-1">
                      <i class="far fa-clock text-muted"></i>
                      <span>{{ tok.booking_time || formatTime(tok.createdAt || tok.date) || '--:--' }}</span>
                      <span v-if="tok.service_name || tok.service?.name">· {{ tok.service_name || tok.service?.name }}</span>
                    </div>
                  </div>
                </div>

                <div class="d-flex align-items-center gap-1">
                  <span class="badge text-xxs px-2 py-1" :class="getTokenStatusBadgeClass(tok.status)">
                    {{ tok.status || 'waiting' }}
                  </span>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-3 text-muted">
              <i class="far fa-calendar-times text-secondary mb-1"></i>
              <p class="text-xxs mb-0">No tokens booked for this date.</p>
            </div>
          </div>
        </div>

        <!-- My shift and room -->
        <div class="md-card p-3 mb-3">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="mb-0 font-weight-bold text-dark">My shift and room</h6>
            <span class="badge bg-success-soft text-success text-xs font-weight-bold px-2 py-1">
              {{ currentDoctorStatusDisplay }}
            </span>
          </div>
          <div class="d-flex align-items-center justify-content-between py-2 border-bottom">
            <span class="text-xs font-weight-bold text-secondary">Shift</span>
            <span class="text-sm font-weight-bold text-dark">{{ currentDoctorShiftDisplay }}</span>
          </div>
          <div class="d-flex align-items-center justify-content-between py-2 border-bottom">
            <span class="text-xs font-weight-bold text-secondary">Room</span>
            <span class="text-sm font-weight-bold text-dark">{{ currentDoctorRoomDisplay }}</span>
          </div>
          <div class="d-flex align-items-center justify-content-between py-2 mb-3">
            <span class="text-xs font-weight-bold text-secondary">Time per patient</span>
            <span class="text-sm font-weight-bold text-dark">{{ currentDoctorSlotDisplay }} min</span>
          </div>
          <button
            type="button"
            class="btn btn-outline-teal w-100 font-weight-bold py-2 mb-0"
            @click="openShiftModal"
          >
            Update shift
          </button>
        </div>

        <!-- Emergency broadcast -->
        <div ref="emergencyPanelDoc" class="md-card p-3 mb-3">
          <div class="mb-2">
            <h6 class="mb-1 font-weight-bold text-dark">Emergency broadcast</h6>
            <small class="text-secondary text-xs d-block">
              {{ emergencyTokens.length ? `${emergencyTokens.length} active emergency alert(s).` : 'No active emergencies. Send an alert to every waiting-hall screen.' }}
            </small>
          </div>

          <div v-if="emergencyTokens.length" class="d-flex flex-column gap-2 mb-2">
            <button
              v-for="em in emergencyTokens"
              :key="em.id"
              type="button"
              class="btn btn-outline-danger d-flex align-items-center justify-content-between p-2 rounded-3 mb-0 text-start w-100"
              @click="broadcastEmergency(em)"
            >
              <div class="text-start">
                <span class="font-weight-bold text-sm text-danger">{{ em.token_number }}</span>
                <small class="text-muted text-xs d-block">{{ em.token_name || 'Emergency patient' }}</small>
              </div>
              <i class="fas fa-ambulance text-danger"></i>
            </button>
          </div>

          <button
            type="button"
            class="btn btn-outline-danger w-100 font-weight-bold py-2 mt-2 mb-0"
            @click="openEmergencyPrompt"
          >
            Send emergency alert
          </button>
        </div>

        <!-- Display screens -->
        <div class="md-card p-3 mb-3">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="mb-0 font-weight-bold text-dark">Display screens</h6>
            <span class="badge bg-success-soft text-success text-xs font-weight-bold px-2 py-1">
              {{ onlineDisplaysCount }} of {{ displays.length }} online
            </span>
          </div>
          <div class="d-flex flex-column gap-2">
            <div
              v-for="screen in displays"
              :key="screen.id"
              class="d-flex align-items-center justify-content-between py-2 border-bottom"
            >
              <span class="text-sm font-weight-bold text-dark">{{ screen.name }}</span>
              <span :class="screen.online ? 'badge bg-success-soft text-success' : 'badge bg-secondary-soft text-secondary'" class="text-xs">
                {{ screen.online ? 'Online' : 'Offline' }}
              </span>
            </div>
            <div v-if="displays.length === 0" class="text-center py-2 text-muted">
              <small>No display screens connected.</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== VOICE & CHIME SETTINGS MODAL ==================== -->
    <div v-if="showVoiceSettingsModal" class="doctor-modal-backdrop" @click.self="showVoiceSettingsModal = false">
      <div class="doctor-modal-card shadow-lg animate-scale-up" style="max-width: 480px;">
        <div class="modal-header-custom border-0 pb-0 d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center gap-2">
            <div class="modal-icon-badge shadow-sm bg-teal text-white p-2 rounded-circle">
              <i class="fas fa-volume-up"></i>
            </div>
            <div>
              <h6 class="mb-0 font-weight-bold text-dark">Audio & Announcement Settings</h6>
              <p class="text-xxs text-secondary mb-0">Configure voice synthesized token calls and chime sound.</p>
            </div>
          </div>
          <button type="button" class="btn-close text-secondary" @click="showVoiceSettingsModal = false" aria-label="Close"></button>
        </div>

        <div class="modal-body-custom pt-3">
          <!-- Chime toggle -->
          <div class="d-flex align-items-center justify-content-between p-2 rounded-3 bg-light mb-3">
            <div>
              <span class="text-xs font-weight-bold text-dark d-block">Play Announcement Chime</span>
              <small class="text-xxs text-secondary">Pleasant harmonic chime before announcing token</small>
            </div>
            <div class="form-check form-switch mb-0">
              <input v-model="voiceSettings.enableChime" class="form-check-input" type="checkbox" role="switch" />
            </div>
          </div>

          <!-- Speech Rate -->
          <div class="mb-3">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <label class="form-label text-xs font-weight-bold text-dark mb-0">Speech Rate</label>
              <span class="text-xxs text-secondary">{{ voiceSettings.rate }}x</span>
            </div>
            <input v-model.number="voiceSettings.rate" type="range" class="form-range" min="0.7" max="1.3" step="0.05" />
          </div>

          <!-- Speech Pitch -->
          <div class="mb-3">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <label class="form-label text-xs font-weight-bold text-dark mb-0">Speech Pitch</label>
              <span class="text-xxs text-secondary">{{ voiceSettings.pitch }}</span>
            </div>
            <input v-model.number="voiceSettings.pitch" type="range" class="form-range" min="0.8" max="1.3" step="0.05" />
          </div>

          <!-- Test Announcement -->
          <button
            type="button"
            class="btn btn-outline-teal w-100 font-weight-bold py-2 mb-2 d-flex align-items-center justify-content-center gap-2"
            @click="testVoiceAnnouncement"
          >
            <i class="fas fa-play"></i>
            <span>Test Voice Announcement</span>
          </button>
        </div>

        <div class="modal-footer-custom border-top pt-2 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-light btn-sm mb-0" @click="showVoiceSettingsModal = false">Close</button>
          <button type="button" class="btn btn-teal text-white btn-sm mb-0 font-weight-bold" @click="saveVoiceSettings">Save Preferences</button>
        </div>
      </div>
    </div>

    <!-- Doctor Availability / Shift Modal -->
    <DoctorShiftModal ref="shiftModal" @session-started="onSessionStarted" />
  </div>
</template>

<script>
import axios from "axios";
import { io } from "socket.io-client";
import Swal from "sweetalert2";
import DoctorShiftModal from "@/components/DoctorShiftModal.vue";

const SERVING_STATUSES = ["serving", "processing"];
const QUEUE_NOW_STATUSES = ["serving", "processing", "calling"];
const QUEUE_NEXT_STATUSES = ["pending", "waiting", "skipped"];

const lower = (v) => String(v || "").toLowerCase();
const stripDr = (v) => lower(v).replace(/^dr\.?\s*/i, "").trim();

const getAuthToken = () =>
  localStorage.getItem("token") || localStorage.getItem("auth_token") || localStorage.getItem("admin_token");
const getApiBase = () => (process.env.VUE_APP_API_BASE_URL || "/api").replace(/\/+$/, "");
const getStoredName = () => localStorage.getItem("userName") || localStorage.getItem("name") || "";
const getStoredEmail = () => localStorage.getItem("adminEmail") || localStorage.getItem("email") || "";

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}") || {};
  } catch (e) {
    return {};
  }
};

const getInitials = (name) =>
  String(name || "")
    .replace(/^dr\.?\s*/i, "")
    .trim()
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase() || "DR";

const formatDoctorName = (rawName) => {
  const raw = String(rawName || "");
  return raw.startsWith("Dr.") ? raw : `Dr. ${raw.replace(/^dr\.?\s*/i, "").trim()}`;
};

const isEmergencyToken = (t) =>
  !!t &&
  lower(t.status) !== "completed" &&
  (t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === "true" || lower(t.is_emergency) === "yes");

const extractTokenDate = (tok) => {
  if (!tok) return "";
  let raw = tok.booking_date || tok.bookingDate || tok.date || tok.token_date;
  if (!raw && (tok.createdAt || tok.created_at)) {
    raw = tok.createdAt || tok.created_at;
  }
  if (!raw) return "";
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }
  const ddmmyyyy = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  if (ddmmyyyy) {
    return `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`;
  }
  try {
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
  } catch (e) {
    // ignore
  }
  return s.split("T")[0].trim();
};

const extractItems = (resVal) => {
  if (!resVal) return [];
  const val = resVal.value !== undefined ? resVal.value : resVal;
  const body = val && val.data !== undefined ? val.data : val;
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const keys = ["data", "result", "tokens", "rows", "users", "candidates", "attendants", "roles", "counters", "branches", "services"];
  for (const k of keys) if (Array.isArray(body[k])) return body[k];
  if (body.data && typeof body.data === "object") {
    for (const k of keys) if (Array.isArray(body.data[k])) return body.data[k];
  }
  return [];
};

export default {
  name: "DoctorDashboard",
  components: {
    DoctorShiftModal,
  },
  data() {
    let savedSession = null;
    try {
      const s = localStorage.getItem("doctorShiftSession");
      if (s) savedSession = JSON.parse(s);
    } catch (e) {
      console.warn("Could not parse doctorShiftSession in data():", e);
    }
    if (!savedSession) {
      savedSession = { start_time: "09:00", end_time: "13:00", slot_duration_minutes: 30 };
    }

    const currentName = getStoredName() || "Doctor";
    const dept = localStorage.getItem("doctorDepartment") || localStorage.getItem("department") || "General OPD";
    const savedRoom = localStorage.getItem("doctorRoom") || localStorage.getItem("counter_name") || savedSession.room || "";
    const savedToken = localStorage.getItem("current_token") || savedSession.current_token || "--";

    const initialDoctor = {
      id: localStorage.getItem("id") || 1,
      name: formatDoctorName(currentName),
      initials: getInitials(currentName),
      email: getStoredEmail() || "doctor@hospital.com",
      speciality: dept,
      department: dept,
      room: savedRoom,
      counter_name: savedRoom,
      currentToken: savedToken,
      shift: `${savedSession.start_time} - ${savedSession.end_time}`,
      slot_duration_minutes: Number(savedSession.slot_duration_minutes) || 30,
      status: localStorage.getItem("availability_status") || "Available",
      statusBadgeClass: "bg-success-soft text-success",
      is_current: true,
    };

    const u = readStoredUser();
    const resolvedTenantName =
      u.company_name || u.tenant_name || u.hospital_name || u.companyShortName ||
      localStorage.getItem("company_name") || localStorage.getItem("tenant_name") || "Hospital";

    return {
      tenantName: resolvedTenantName,
      activeEmergency: null,
      activeDoctorSession: savedSession,
      currentTime: new Date(),
      timerInterval: null,
      socket: null,
      isFetchingSession: false,
      doctors: [initialDoctor],
      displays: [],
      emergencyTokens: [],
      counters: [],
      services: [],
      todayTokens: [],
      localSkippedTokenIds: (() => {
        try {
          const raw = localStorage.getItem("local_skipped_token_ids");
          return raw ? JSON.parse(raw) : [];
        } catch (e) {
          return [];
        }
      })(),
      isCallingNext: false,
      isUpdatingQueue: false,

      // Calendar State
      calendarCurrentMonth: new Date().getMonth(),
      calendarCurrentYear: new Date().getFullYear(),
      selectedCalendarDate: (() => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return localStorage.getItem("doctorCalendarDate") || `${y}-${m}-${day}`;
      })(),
      calendarFetchedTokens: [],
      isFetchingCalendarTokens: false,
      calendarViewMode: "list",
      showVoiceSettingsModal: false,
      voiceSettings: (() => {
        try {
          const s = localStorage.getItem("doctor_voice_settings");
          return s ? JSON.parse(s) : { enableChime: true, rate: 0.95, pitch: 1.0 };
        } catch (e) {
          return { enableChime: true, rate: 0.95, pitch: 1.0 };
        }
      })(),

      doctorHeaderStatusOptions: [
        { value: "Available", label: "Available", activeClass: "btn-segment-available" },
        { value: "Busy", label: "Busy", activeClass: "btn-segment-busy" },
        { value: "On Break", label: "Break", activeClass: "btn-segment-break" },
        { value: "Offline", label: "Offline", activeClass: "btn-segment-offline" },
      ],
    };
  },
  computed: {
    currentTenantId() {
      const u = readStoredUser();
      const raw =
        u.tenant_id ??
        u.tenantId ??
        u.TenantId ??
        localStorage.getItem("tenant_id") ??
        localStorage.getItem("tenantId");
      if (raw === null || raw === undefined || raw === "") return null;
      return String(raw).trim();
    },
    resolvedHospitalName() {
      return this.tenantName || "Hospital";
    },
    doctorGreeting() {
      const now = this.currentTime || new Date();
      const hour = now.getHours();
      let greeting = "Good morning";
      if (hour >= 12 && hour < 17) greeting = "Good afternoon";
      else if (hour >= 17) greeting = "Good evening";
      const doc = this.doctors && this.doctors[0];
      const docName = (doc && (doc.doctor_name || doc.name)) || formatDoctorName(getStoredName() || "Doctor");
      return `${greeting}, ${docName}`;
    },
    doctorSubheader() {
      const now = this.currentTime || new Date();
      const dateStr = now.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "short",
      });
      const waiting = this.visibleTokens.filter((t) => QUEUE_NEXT_STATUSES.includes(lower(t.status))).length;
      return `${dateStr} · ${waiting} ${waiting === 1 ? "patient" : "patients"} waiting`;
    },
    currentDoctorStatusDisplay() {
      const d = this.doctors && this.doctors[0];
      return (d && d.status) || localStorage.getItem("availability_status") || "Available";
    },
    currentDoctorShiftDisplay() {
      const d = this.doctors && this.doctors[0];
      if (d && d.shift) return d.shift;
      if (this.activeDoctorSession) {
        return `${this.activeDoctorSession.start_time} - ${this.activeDoctorSession.end_time}`;
      }
      return "09:00 - 13:00";
    },
    currentDoctorSlotDisplay() {
      const d = this.doctors && this.doctors[0];
      if (d && d.slot_duration_minutes) return d.slot_duration_minutes;
      if (this.activeDoctorSession && this.activeDoctorSession.slot_duration_minutes) {
        return this.activeDoctorSession.slot_duration_minutes;
      }
      return 30;
    },
    currentDoctorRoomDisplay() {
      const d = this.doctors && this.doctors[0];
      const storedRoom = localStorage.getItem("doctorRoom") || localStorage.getItem("counter_name");
      let room = (d && (d.room || d.counter_name)) || storedRoom || "";
      if (!room && Array.isArray(this.counters) && this.counters.length > 0) {
        const myId = String(localStorage.getItem("id") || "");
        const myName = stripDr(getStoredName());
        const matched = this.counters.find((c) => {
          const cUId = String(c.user_id || (c.user ? c.user.id : ""));
          const cName = stripDr(c.user?.name || c.attendant_name || "");
          return (myId && cUId === myId) || (myName && cName && cName.includes(myName));
        });
        if (matched) room = matched.name;
      }
      return room || "General Medicine Counter";
    },
    visibleTokens() {
      const activeDate = this.selectedCalendarDate || this.todayDateIso;
      const u = readStoredUser();
      const me = this.doctors && this.doctors[0];
      const currentUserId = String(u.id || localStorage.getItem("id") || localStorage.getItem("userId") || (me ? me.id : ""));
      const currentUserName = stripDr(u.name || getStoredName() || (me ? me.name : ""));
      const currentUserEmail = lower(u.email || getStoredEmail() || (me ? me.email : "")).trim();

      const myCounterIds = new Set();
      const myRoomNames = new Set();

      const storedCounterId = localStorage.getItem("counter_id");
      if (storedCounterId) myCounterIds.add(String(storedCounterId));

      const storedRoom = localStorage.getItem("doctorRoom") || localStorage.getItem("counter_name");
      if (storedRoom) myRoomNames.add(lower(storedRoom).replace(/^room\s*/i, "").trim());

      let docDeptClean = "";
      if (this.doctors.length > 0) {
        const d = this.doctors[0];
        if (d.counter_id) myCounterIds.add(String(d.counter_id));
        if (d.room) myRoomNames.add(lower(d.room).replace(/^room\s*/i, "").trim());
        if (d.counter_name) myRoomNames.add(lower(d.counter_name).replace(/^room\s*/i, "").trim());
        docDeptClean = lower(d.department || d.speciality || "").trim();
      }

      const myServiceIds = new Set();
      if (Array.isArray(this.counters)) {
        this.counters.forEach((c) => {
          if (this.isOtherTenant(c)) return;
          const cUserId = String(c.user_id || (c.user ? c.user.id : "") || "");
          const cAttName = stripDr(c.user?.name || c.attendant_name);
          const cEmail = lower(c.user?.email || c.email).trim();
          const matchesUser = currentUserId && cUserId === currentUserId;
          const matchesEmail = currentUserEmail && cEmail && cEmail === currentUserEmail;
          const matchesAttendant = currentUserName && cAttName && (cAttName.includes(currentUserName) || currentUserName.includes(cAttName));
          const matchesRoom = myRoomNames.size > 0 && c.name && myRoomNames.has(lower(c.name).replace(/^room\s*/i, "").trim());

          if (matchesUser || matchesEmail || matchesAttendant || matchesRoom) {
            if (c.id) myCounterIds.add(String(c.id));
            if (c.name) myRoomNames.add(lower(c.name).replace(/^room\s*/i, "").trim());

            let sIds = c.service_ids;
            while (typeof sIds === "string") {
              try {
                const parsed = JSON.parse(sIds);
                if (parsed === sIds) break;
                sIds = parsed;
              } catch (e) {
                sIds = sIds.split(",").map((s) => s.trim());
                break;
              }
            }
            if (Array.isArray(sIds)) {
              sIds.forEach((id) => myServiceIds.add(Number(id)));
            }
          }
        });
      }

      const tokenList = [];
      const seenIds = new Set();
      const pushToken = (t) => {
        if (!t) return;
        if (t.id) {
          if (!seenIds.has(t.id)) {
            seenIds.add(t.id);
            tokenList.push(t);
          }
        } else {
          tokenList.push(t);
        }
      };
      (this.todayTokens || []).forEach(pushToken);
      (this.calendarFetchedTokens || []).forEach(pushToken);

      return tokenList.filter((t) => {
        if (this.isOtherTenant(t)) return false;

        // Strict date check: only show tokens whose booking or creation date matches activeDate
        const tDate = extractTokenDate(t);
        if (tDate && activeDate && tDate !== activeDate) {
          return false;
        }
        if (!tDate && activeDate !== this.todayDateIso) {
          return false;
        }

        if (isEmergencyToken(t)) return true;

        const uId = String(t.user_id || "");
        const cId = String(t.counter_id || "");
        const tAtt = stripDr(t.token_attended);
        const tRoom = lower(t.counter?.name || t.counter_name || t.room || "").replace(/^room\s*/i, "").trim();

        const matchesUser = currentUserId && (uId === currentUserId || String(t.created_by || "") === currentUserId);
        const matchesCounter = cId && myCounterIds.has(cId);
        const matchesRoom = tRoom && myRoomNames.has(tRoom);
        const matchesAttendant = tAtt && tAtt !== "no" && (
          (currentUserName && (tAtt.includes(currentUserName) || currentUserName.includes(tAtt))) ||
          (u.name && (tAtt.includes(stripDr(u.name)) || stripDr(u.name).includes(tAtt))) ||
          (me && me.name && (tAtt.includes(stripDr(me.name)) || stripDr(me.name).includes(tAtt)))
        );

        if (matchesUser || matchesCounter || matchesRoom || matchesAttendant) {
          return true;
        }

        const st = lower(t.status || "");
        const isWaiting =
          QUEUE_NEXT_STATUSES.includes(st) ||
          st === "pending" ||
          st === "waiting" ||
          st === "skipped" ||
          this.isSkippedToken(t);
        if (isWaiting) {
          const isUnassigned = !uId || uId === "0" || !tAtt || tAtt === "no";
          const tServiceId = Number(t.service_id || t.service?.id || 0);
          if (tServiceId && myServiceIds.has(tServiceId)) return true;

          if (docDeptClean) {
            const tDept = lower(t.department || t.speciality || t.branch_name || t.service?.branch_name || t.service?.name || "").trim();
            if (tDept && (tDept.includes(docDeptClean) || docDeptClean.includes(tDept))) return true;
          }

          if (isUnassigned && (!cId || cId === "0" || cId === "null" || myCounterIds.has(cId))) {
            return true;
          }

          // If no specific counter is bound or queue is general, show waiting tokens
          if (myCounterIds.size === 0) {
            return true;
          }
        }

        return false;
      });
    },
    waitingTokensCount() {
      return this.waitingTokensList.length;
    },
    skippedTokensCount() {
      return this.visibleTokens.filter((t) => this.isSkippedToken(t)).length;
    },
    waitingTokensList() {
      return this.visibleTokens.filter(
        (t) => QUEUE_NEXT_STATUSES.includes(lower(t.status)) || this.isSkippedToken(t)
      );
    },
    currentlyServingToken() {
      const active = this.visibleTokens.find((t) => QUEUE_NOW_STATUSES.includes(lower(t.status)));
      if (active) return active;
      const me = this.doctors && this.doctors[0];
      if (me && me.currentToken && me.currentToken !== "--") {
        return this.visibleTokens.find((t) => String(t.token_number) === String(me.currentToken)) || null;
      }
      return null;
    },
    isCurrentTokenCalling() {
      return lower(this.currentlyServingToken?.status) === "calling";
    },
    isCurrentTokenServing() {
      return SERVING_STATUSES.includes(lower(this.currentlyServingToken?.status));
    },
    servingDurationDisplay() {
      if (!this.currentlyServingToken) return "0 min";
      const start = new Date(this.currentlyServingToken.updatedAt || this.currentlyServingToken.created_at || Date.now());
      const diffMin = Math.max(1, Math.floor(((this.currentTime || new Date()).getTime() - start.getTime()) / 60000));
      return `${diffMin} min`;
    },
    servingProgressPct() {
      if (!this.currentlyServingToken) return 0;
      const slot = Number(this.currentDoctorSlotDisplay) || 30;
      const start = new Date(this.currentlyServingToken.updatedAt || this.currentlyServingToken.created_at || Date.now());
      const elapsedMin = Math.max(1, Math.floor(((this.currentTime || new Date()).getTime() - start.getTime()) / 60000));
      return Math.min(100, Math.round((elapsedMin / slot) * 100));
    },
    currentlyServingService() {
      if (!this.currentlyServingToken) return "General Consultation";
      return (
        this.currentlyServingToken.service?.name ||
        this.currentlyServingToken.service_name ||
        this.currentlyServingToken.counter?.name ||
        "Consultation"
      );
    },
    nextWaitingToken() {
      const unskipped = this.waitingTokensList.filter((t) => !this.isSkippedToken(t));
      if (unskipped.length > 0) return unskipped[0];
      return this.waitingTokensList[0] || null;
    },
    emergencyPendingTokens() {
      return this.visibleTokens.filter(
        (t) => isEmergencyToken(t) && QUEUE_NEXT_STATUSES.includes(lower(t.status))
      );
    },
    hasEmergencyTokens() {
      return this.emergencyPendingTokens.length > 0;
    },
    onlineDisplaysCount() {
      return this.displays.filter((s) => s.online).length;
    },
    kpiCards() {
      const total = this.visibleTokens.length;
      const waiting = this.waitingTokensCount;
      const serving = this.currentlyServingToken ? 1 : 0;
      const completed = this.visibleTokens.filter((t) => lower(t.status) === "completed").length;
      const skipped = this.skippedTokensCount;

      return [
        {
          key: "total",
          label: "Total Patients",
          value: total,
          sub: "Today's queue",
          subClass: "text-muted",
          icon: "fas fa-users",
          tone: "tone-blue",
        },
        {
          key: "waiting",
          label: "Waiting in Queue",
          value: waiting,
          sub: `${waiting} pending call`,
          subClass: "text-warning",
          icon: "fas fa-clock",
          tone: "tone-amber",
        },
        {
          key: "serving",
          label: "In Consultation",
          value: serving,
          sub: serving ? "Active patient in room" : "Room ready",
          subClass: "text-teal",
          icon: "fas fa-user-md",
          tone: "tone-teal",
        },
        {
          key: "completed",
          label: "Consulted Today",
          value: completed,
          sub: `${completed} finished`,
          subClass: "text-success",
          icon: "fas fa-check-circle",
          tone: "tone-pink",
        },
        {
          key: "skipped",
          label: "Skipped",
          value: skipped,
          sub: `${skipped} skipped`,
          subClass: "text-danger",
          icon: "fas fa-forward",
          tone: "tone-rose",
        },
      ];
    },
    todayDateIso() {
      const d = new Date();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formattedSelectedCalendarDate() {
      if (!this.selectedCalendarDate) return "";
      try {
        const parts = this.selectedCalendarDate.split("-");
        if (parts.length === 3) {
          const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
          return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
        }
      } catch (e) {
        // fallback
      }
      return this.selectedCalendarDate;
    },
    calendarMonthYearHeader() {
      const d = new Date(this.calendarCurrentYear, this.calendarCurrentMonth, 1);
      return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    },
    doctorBookedTokensMap() {
      const map = {};
      const allTokens = [...(this.todayTokens || []), ...(this.calendarFetchedTokens || [])];
      const seenIds = new Set();

      allTokens.forEach((tok) => {
        if (!tok || !tok.id) return;
        if (seenIds.has(tok.id)) return;
        seenIds.add(tok.id);

        if (!this.isTokenForCurrentDoctor(tok)) return;

        const dateStr = extractTokenDate(tok);
        if (dateStr) {
          if (!map[dateStr]) map[dateStr] = [];
          map[dateStr].push(tok);
        }
      });

      return map;
    },
    selectedDateTokens() {
      if (!this.selectedCalendarDate) return [];
      const list = this.doctorBookedTokensMap[this.selectedCalendarDate] || [];
      return [...list].sort((a, b) => {
        const tA = a.booking_time || a.createdAt || "";
        const tB = b.booking_time || b.createdAt || "";
        return tA.localeCompare(tB);
      });
    },
    calendarDays() {
      const year = this.calendarCurrentYear;
      const month = this.calendarCurrentMonth;

      const firstDayOfMonth = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const daysInPrevMonth = new Date(year, month, 0).getDate();

      const days = [];

      // 1. Previous month trailing days
      for (let i = firstDayOfMonth - 1; i >= 0; i--) {
        const dayNum = daysInPrevMonth - i;
        const prevDate = new Date(year, month - 1, dayNum);
        const y = prevDate.getFullYear();
        const m = String(prevDate.getMonth() + 1).padStart(2, "0");
        const d = String(dayNum).padStart(2, "0");
        const dateStr = `${y}-${m}-${d}`;
        const dayTokens = this.doctorBookedTokensMap[dateStr] || [];
        days.push({
          day: dayNum,
          dateStr,
          isCurrentMonth: false,
          isToday: dateStr === this.todayDateIso,
          isSelected: dateStr === this.selectedCalendarDate,
          tokenCount: dayTokens.length,
          hasTokens: dayTokens.length > 0,
        });
      }

      // 2. Current month days
      for (let i = 1; i <= daysInMonth; i++) {
        const y = year;
        const m = String(month + 1).padStart(2, "0");
        const d = String(i).padStart(2, "0");
        const dateStr = `${y}-${m}-${d}`;
        const dayTokens = this.doctorBookedTokensMap[dateStr] || [];
        days.push({
          day: i,
          dateStr,
          isCurrentMonth: true,
          isToday: dateStr === this.todayDateIso,
          isSelected: dateStr === this.selectedCalendarDate,
          tokenCount: dayTokens.length,
          hasTokens: dayTokens.length > 0,
        });
      }

      // 3. Next month leading days to complete full 6 weeks grid (42 days)
      const remainingDays = 42 - days.length;
      for (let i = 1; i <= remainingDays; i++) {
        const nextDate = new Date(year, month + 1, i);
        const y = nextDate.getFullYear();
        const m = String(nextDate.getMonth() + 1).padStart(2, "0");
        const d = String(i).padStart(2, "0");
        const dateStr = `${y}-${m}-${d}`;
        const dayTokens = this.doctorBookedTokensMap[dateStr] || [];
        days.push({
          day: i,
          dateStr,
          isCurrentMonth: false,
          isToday: dateStr === this.todayDateIso,
          isSelected: dateStr === this.selectedCalendarDate,
          tokenCount: dayTokens.length,
          hasTokens: dayTokens.length > 0,
        });
      }

      return days;
    },
    calendarTimeSlots() {
      const shiftStr = this.currentDoctorShiftDisplay || "09:00 - 17:00";
      const slotMins = Number(this.currentDoctorSlotDisplay) || 30;
      let [startStr, endStr] = shiftStr.split("-").map((s) => s.trim());
      if (!startStr) startStr = "09:00";
      if (!endStr) endStr = "17:00";

      const [sH, sM] = startStr.split(":").map(Number);
      const [eH, eM] = endStr.split(":").map(Number);
      const startMinutes = (isNaN(sH) ? 9 : sH) * 60 + (isNaN(sM) ? 0 : sM);
      const endMinutes = (isNaN(eH) ? 17 : eH) * 60 + (isNaN(eM) ? 0 : eM);

      const slots = [];
      const tokens = this.selectedDateTokens || [];

      for (let min = startMinutes; min < endMinutes; min += slotMins) {
        const h = Math.floor(min / 60);
        const m = min % 60;
        const timeStr = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

        const matchedToken = tokens.find((t) => {
          if (!t.booking_time) return false;
          return t.booking_time.startsWith(timeStr);
        });

        slots.push({
          time: timeStr,
          minutes: min,
          token: matchedToken || null,
        });
      }
      return slots;
    },
  },
  mounted() {
    this.timerInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 1000);

    // Auto-poll queue in background every 5 seconds for real-time sync
    this.pollInterval = setInterval(() => {
      this.loadDoctorSession(true);
    }, 5000);

    this.initSocket();
    this.loadDoctorSession();
    this.loadCalendarTokens();

    this.onTokenSyncHandler = () => {
      this.loadDoctorSession(true);
      this.loadCalendarTokens();
    };
    window.addEventListener("token-created", this.onTokenSyncHandler);
    window.addEventListener("tokens-updated", this.onTokenSyncHandler);

    window.addEventListener("storage", this.handleStorageEvent);
    window.addEventListener("doctor-availability-changed", this.handleDoctorAvailabilityEvent);
    window.addEventListener("doctor-status-synced", this.handleDoctorStatusSynced);
  },
  beforeUnmount() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.pollInterval) clearInterval(this.pollInterval);
    if (this.socket) this.socket.disconnect();

    if (this.onTokenSyncHandler) {
      window.removeEventListener("token-created", this.onTokenSyncHandler);
      window.removeEventListener("tokens-updated", this.onTokenSyncHandler);
    }

    window.removeEventListener("storage", this.handleStorageEvent);
    window.removeEventListener("doctor-availability-changed", this.handleDoctorAvailabilityEvent);
    window.removeEventListener("doctor-status-synced", this.handleDoctorStatusSynced);
  },
  methods: {
    isOtherTenant(rec) {
      if (!rec) return false;
      const myTenantId = this.currentTenantId;
      if (!myTenantId) return false;
      const recTenantId =
        rec.tenant_id ??
        rec.tenantId ??
        rec.TenantId ??
        rec.tenant?.id ??
        rec.Tenant?.id ??
        (rec.user && (rec.user.tenant_id ?? rec.user.tenantId)) ??
        null;
      if (recTenantId === null || recTenantId === undefined) return false;
      const s = String(recTenantId).trim();
      if (!s || s === "null" || s === "undefined" || s === "0") return false;
      return s !== String(myTenantId);
    },
    initSocket() {
      try {
        const rawBase = process.env.VUE_APP_API_BASE_URL || window.location.origin;
        const parsedUrl = new URL(rawBase.startsWith("http") ? rawBase : window.location.origin);
        const socketUrl = process.env.VUE_APP_SOCKET_URL || `${parsedUrl.protocol}//${parsedUrl.host}`;

        this.socket = io(socketUrl, {
          auth: { token: getAuthToken() },
          transports: ["websocket", "polling"],
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 2000,
        });

        const syncTokens = () => this.loadDoctorSession(true);

        this.socket.on("doctor_status_updated", syncTokens);
        this.socket.on("doctor_availability_changed", syncTokens);
        this.socket.on("counter_status_updated", syncTokens);
        this.socket.on("doctor_shift_updated", syncTokens);
        this.socket.on("token_updated", syncTokens);
        this.socket.on("token_created", syncTokens);
      } catch (err) {
        console.warn("Could not init socket in DoctorDashboard:", err);
      }
    },
    handleStorageEvent(e) {
      if (!e) return;
      if (e.key === "tokens_last_updated" || e.key === "tokensUpdated") {
        this.loadDoctorSession(true);
        this.loadCalendarTokens();
      }
      if (e.key === "availability_status" || e.key === "doctor_status") {
        if (this.doctors[0]) this.doctors[0].status = e.newValue;
      }
    },
    handleDoctorAvailabilityEvent(e) {
      if (e.detail?.status && this.doctors[0]) {
        this.doctors[0].status = e.detail.status;
      }
    },
    handleDoctorStatusSynced(e) {
      if (e.detail?.status && this.doctors[0]) {
        this.doctors[0].status = e.detail.status;
      }
    },
    isCurrentDoctorStatus(val) {
      return lower(this.currentDoctorStatusDisplay) === lower(val);
    },
    async setDoctorHeaderStatus(status) {
      const me = this.doctors && this.doctors[0];
      if (me) me.status = status;
      localStorage.setItem("availability_status", status);
      localStorage.setItem("doctor_status", status);

      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        await axios.post(
          `${apiUrl}/v1/admin/doctor/availability`,
          {
            doctor_id: me ? me.id : localStorage.getItem("id"),
            status,
            availability_status: status,
          },
          { headers }
        );
      } catch (err) {
        console.warn("Backend status update non-fatal:", err.message);
      }

      window.dispatchEvent(
        new CustomEvent("doctor-status-synced", {
          detail: { doctor_id: me?.id, status, availability_status: status },
        })
      );
    },
    openShiftModal() {
      if (this.$refs.shiftModal) {
        this.$refs.shiftModal.open();
      }
    },
    onSessionStarted(sessionData) {
      this.activeDoctorSession = sessionData;
      localStorage.setItem("doctorShiftSession", JSON.stringify(sessionData));
      if (this.doctors[0]) {
        this.applyShiftToDoctor(this.doctors[0], sessionData, true);
        if (sessionData.department) {
          localStorage.setItem("doctorDepartment", sessionData.department);
          localStorage.setItem("department", sessionData.department);
        }
        if (sessionData.room) {
          localStorage.setItem("doctorRoom", sessionData.room);
          localStorage.setItem("counter_name", sessionData.room);
        }
        const docId = String(this.doctors[0].id || localStorage.getItem("id") || "");
        const docEmail = lower(this.doctors[0].email || getStoredEmail()).trim();
        this.saveShiftRecord([docId, docEmail, stripDr(this.doctors[0].name)], sessionData);
      }
      if (this.socket && this.socket.connected) {
        this.socket.emit("doctor_shift_updated", sessionData);
        this.socket.emit("doctor_status_updated", sessionData);
      }
      this.loadDoctorSession(true);
    },
    findSavedShiftForDoctor(id, email, name) {
      const strId = String(id || "").trim();
      const cleanEmail = lower(email).trim();
      const cleanName = stripDr(name);
      let all = {};
      try {
        all = JSON.parse(localStorage.getItem("hospitalDoctorShifts") || "{}") || {};
      } catch (e) {
        all = {};
      }

      try {
        for (const k of [strId, cleanEmail, cleanName]) {
          if (!k) continue;
          const cached = localStorage.getItem(`doctorShift_${k}`);
          if (cached) return JSON.parse(cached);
          if (all[k]) return all[k];
        }

        const found = Object.values(all).find((item) => {
          if (!item) return false;
          if (strId && [item.doctor_id, item.id, item.user_id].some((x) => String(x) === strId)) return true;
          if (cleanEmail && item.doctor_email && lower(item.doctor_email) === cleanEmail) return true;
          if (cleanName && item.doctor_name) {
            const c = stripDr(item.doctor_name);
            if (c.includes(cleanName) || cleanName.includes(c)) return true;
          }
          return false;
        });
        if (found) return found;

        const sessionStr = localStorage.getItem("doctorShiftSession");
        if (sessionStr) {
          const s = JSON.parse(sessionStr);
          if (
            (s.doctor_email && cleanEmail && lower(s.doctor_email) === cleanEmail) ||
            (s.doctor_id && strId && String(s.doctor_id) === strId) ||
            (s.doctor_name && cleanName && stripDr(s.doctor_name).includes(cleanName))
          ) {
            return s;
          }
        }
      } catch (e) {
        console.warn("Could not find saved shift:", e.message);
      }
      return null;
    },
    saveShiftRecord(keys, data) {
      let all = {};
      try {
        all = JSON.parse(localStorage.getItem("hospitalDoctorShifts") || "{}") || {};
      } catch (e) {
        all = {};
      }
      keys.filter(Boolean).forEach((k) => {
        localStorage.setItem(`doctorShift_${k}`, JSON.stringify(data));
        all[k] = data;
      });
      localStorage.setItem("hospitalDoctorShifts", JSON.stringify(all));
    },
    applyShiftToDoctor(doc, s, padRoom = false) {
      if (!doc || !s) return;
      const shLabel = s.start_time && s.end_time ? `${s.start_time} - ${s.end_time}` : s.shift || "09:00 - 17:00";
      doc.shift = shLabel;
      if (s.start_time) doc.start_time = s.start_time;
      if (s.end_time) doc.end_time = s.end_time;
      if (s.slot_duration_minutes) doc.slot_duration_minutes = Number(s.slot_duration_minutes);
      if (s.room) {
        const digits = String(s.room).replace(/[^0-9]/g, "");
        doc.room = padRoom && digits ? digits.padStart(2, "0") : s.room;
        doc.counter_name = doc.room;
      }
      if (s.department) {
        doc.speciality = s.department;
        doc.department = s.department;
      }
      if (s.status) this.setDocStatus(doc, s.status);
    },
    getDoctorShift(doc) {
      if (!doc) return "09:00 - 17:00";
      if (doc.start_time && doc.end_time) return `${doc.start_time} - ${doc.end_time}`;
      if (doc.shift && doc.shift !== "09:00 - 17:00") return doc.shift;
      const saved = this.findSavedShiftForDoctor(doc.user_id || doc.id, doc.email, doc.doctor_name || doc.name);
      if (saved && (saved.start_time || saved.shift)) {
        return saved.start_time && saved.end_time ? `${saved.start_time} - ${saved.end_time}` : saved.shift;
      }
      return doc.shift || "09:00 - 17:00";
    },
    getDoctorSlot(doc) {
      if (!doc) return 30;
      if (doc.slot_duration_minutes) return Number(doc.slot_duration_minutes);
      const saved = this.findSavedShiftForDoctor(doc.user_id || doc.id, doc.email, doc.doctor_name || doc.name);
      if (saved?.slot_duration_minutes) return Number(saved.slot_duration_minutes);
      return 30;
    },
    isTokenForCurrentDoctor(t) {
      if (!t) return false;
      const currentUserId = String(localStorage.getItem("id") || localStorage.getItem("userId") || "");
      const currentUserName = stripDr(getStoredName());
      const currentUserEmail = lower(getStoredEmail()).trim();
      const storedCounterId = localStorage.getItem("counter_id");
      const storedRoom = localStorage.getItem("doctorRoom") || localStorage.getItem("counter_name");
      const myRoom = storedRoom ? lower(storedRoom).replace(/^room\s*/i, "").trim() : "";

      const doc = this.doctors && this.doctors[0];
      const docCounterId = doc && doc.counter_id ? String(doc.counter_id) : "";
      const docRoom = doc && (doc.room || doc.counter_name) ? lower(doc.room || doc.counter_name).replace(/^room\s*/i, "").trim() : "";
      const docDeptClean = doc ? lower(doc.department || doc.speciality || "").trim() : "";

      const uId = String(t.user_id || "");
      const cId = String(t.counter_id || "");
      const tAtt = stripDr(t.token_attended);
      const tRoom = lower(t.counter?.name || t.counter_name || t.room || "").replace(/^room\s*/i, "").trim();

      if (currentUserId && (uId === currentUserId || (t.user && String(t.user.id) === currentUserId))) return true;
      if (currentUserEmail && t.user?.email && lower(t.user.email).trim() === currentUserEmail) return true;
      if (currentUserName && tAtt && tAtt !== "no" && (tAtt.includes(currentUserName) || currentUserName.includes(tAtt))) return true;

      if (storedCounterId && cId === String(storedCounterId)) return true;
      if (docCounterId && cId === docCounterId) return true;
      if (myRoom && tRoom && (myRoom === tRoom || tRoom.includes(myRoom))) return true;
      if (docRoom && tRoom && (docRoom === tRoom || tRoom.includes(docRoom))) return true;

      if (docDeptClean) {
        const tDept = lower(t.department || t.speciality || t.branch_name || t.service?.branch_name || t.service?.name || "").trim();
        if (tDept && (tDept.includes(docDeptClean) || docDeptClean.includes(tDept))) return true;
      }

      const st = lower(t.status || "");
      if (QUEUE_NEXT_STATUSES.includes(st) || st === "pending" || st === "waiting") {
        if (!uId || uId === "0" || !tAtt || tAtt === "no") return true;
      }

      return false;
    },
    async loadCalendarTokens() {
      await this.fetchCalendarTokens(this.selectedCalendarDate);
      await this.fetchAllDoctorTokensForCalendar();
    },
    async fetchAllDoctorTokensForCalendar() {
      const token = getAuthToken();
      if (!token) return;
      try {
        const base = getApiBase();
        const url = `${base}/v1/admin/tokens/list?all=true`;
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const items = extractItems(res);
        if (Array.isArray(items)) {
          const currentMap = new Map((this.calendarFetchedTokens || []).map((t) => [t.id, t]));
          items.forEach((tok) => {
            if (tok && tok.id) currentMap.set(tok.id, tok);
          });
          this.calendarFetchedTokens = Array.from(currentMap.values());
        }
      } catch (err) {
        console.warn("Could not fetch all calendar tokens:", err);
      }
    },
    async fetchCalendarTokens(dateStr) {
      const targetDate = dateStr || this.selectedCalendarDate || this.todayDateIso;
      const token = getAuthToken();
      if (!token) return;
      this.isFetchingCalendarTokens = true;
      try {
        const base = getApiBase();
        const url = `${base}/v1/admin/tokens/list?date=${targetDate}&booking_date=${targetDate}&all=true`;
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const items = extractItems(res);
        if (Array.isArray(items)) {
          const currentMap = new Map((this.calendarFetchedTokens || []).map((t) => [t.id, t]));
          items.forEach((tok) => {
            if (tok && tok.id) currentMap.set(tok.id, tok);
          });
          this.calendarFetchedTokens = Array.from(currentMap.values());
        }
      } catch (err) {
        console.warn("Could not fetch calendar tokens:", targetDate, err);
      } finally {
        this.isFetchingCalendarTokens = false;
      }
    },
    prevCalendarMonth() {
      if (this.calendarCurrentMonth === 0) {
        this.calendarCurrentMonth = 11;
        this.calendarCurrentYear -= 1;
      } else {
        this.calendarCurrentMonth -= 1;
      }
      this.fetchAllDoctorTokensForCalendar();
    },
    nextCalendarMonth() {
      if (this.calendarCurrentMonth === 11) {
        this.calendarCurrentMonth = 0;
        this.calendarCurrentYear += 1;
      } else {
        this.calendarCurrentMonth += 1;
      }
      this.fetchAllDoctorTokensForCalendar();
    },
    async selectCalendarDate(dayObj) {
      if (!dayObj || !dayObj.dateStr) return;
      this.selectedCalendarDate = dayObj.dateStr;
      localStorage.setItem("doctorCalendarDate", dayObj.dateStr);

      const parts = dayObj.dateStr.split("-");
      this.calendarCurrentYear = Number(parts[0]);
      this.calendarCurrentMonth = Number(parts[1]) - 1;

      if (this.activeDoctorSession) {
        this.activeDoctorSession.date = dayObj.dateStr;
      }
      await this.fetchCalendarTokens(dayObj.dateStr);
    },
    async resetCalendarToToday() {
      const now = new Date();
      this.calendarCurrentYear = now.getFullYear();
      this.calendarCurrentMonth = now.getMonth();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      this.selectedCalendarDate = `${y}-${m}-${d}`;
      localStorage.setItem("doctorCalendarDate", this.selectedCalendarDate);
      if (this.activeDoctorSession) {
        this.activeDoctorSession.date = this.selectedCalendarDate;
      }
      await this.fetchCalendarTokens(this.selectedCalendarDate);
    },
    playAnnouncementChime() {
      if (!this.voiceSettings?.enableChime) return;
      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.frequency.setValueAtTime(587.33, audioCtx.currentTime); // D5
        osc.frequency.setValueAtTime(880, audioCtx.currentTime + 0.15); // A5
        gain.gain.setValueAtTime(0.15, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.4);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.4);
      } catch (e) {
        // AudioContext not available
      }
    },
    speakAnnouncement(text) {
      if (typeof window === "undefined" || !window.speechSynthesis) return;
      try {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = this.voiceSettings?.rate || 0.95;
        utterance.pitch = this.voiceSettings?.pitch || 1.0;
        window.speechSynthesis.speak(utterance);
      } catch (e) {
        console.warn("Speech synthesis error:", e);
      }
    },
    openVoiceSettings() {
      this.showVoiceSettingsModal = true;
    },
    saveVoiceSettings() {
      try {
        localStorage.setItem("doctor_voice_settings", JSON.stringify(this.voiceSettings));
        Swal.fire({
          title: "Audio Settings Saved",
          text: "Announcement rate and pitch preferences have been saved.",
          icon: "success",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
      } catch (e) {
        console.warn("Could not save voice settings:", e);
      }
      this.showVoiceSettingsModal = false;
    },
    testVoiceAnnouncement() {
      if (this.voiceSettings.enableChime) {
        this.playAnnouncementChime();
      }
      const testText = `Token number A 1 0 1, please proceed to consultation room ${this.currentDoctorRoomDisplay}`;
      this.speakAnnouncement(testText);
    },
    getTokenStatusBadgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "completed" || s === "done") return "bg-success-soft text-success";
      if (["pending", "waiting", "calling", "serving", "processing"].includes(s)) return "bg-warning-soft text-warning";
      if (s === "skipped") return "bg-secondary-soft text-secondary";
      if (s === "rejected") return "bg-danger-soft text-danger";
      return "bg-light text-secondary";
    },
    formatTime(val) {
      if (!val) return "--";
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val);
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      } catch (e) {
        return String(val);
      }
    },
    scrollToEmergency() {
      if (this.$refs.emergencyPanelDoc) {
        this.$refs.emergencyPanelDoc.scrollIntoView({ behavior: "smooth" });
      }
    },
    formatPatientNameAndAge(tok) {
      if (!tok) return "Walk-in";
      const name = tok.token_name || "Walk-in";
      const age = tok.age !== undefined && tok.age !== null && tok.age !== "" ? `${tok.age}` : "";
      let gender = "";
      if (tok.gender) {
        const g = String(tok.gender).trim().toUpperCase();
        if (g.startsWith("M")) gender = "M";
        else if (g.startsWith("F")) gender = "F";
        else gender = g;
      }
      const ageGender = [age, gender].filter(Boolean).join(" ");
      return ageGender ? `${name}, ${ageGender}` : name;
    },
    getWaitingMinutes(tok) {
      if (!tok) return 1;
      const created = new Date(tok.createdAt || tok.created_at || Date.now());
      const diffMs = (this.currentTime || new Date()).getTime() - created.getTime();
      return Math.max(1, Math.floor(diffMs / 60000));
    },
    isSkippedToken(tok) {
      if (!tok) return false;
      return (
        lower(tok.status) === "skipped" ||
        tok.is_skipped === true ||
        tok.is_skipped === 1 ||
        String(tok.is_skipped || "").toLowerCase() === "true" ||
        (Array.isArray(this.localSkippedTokenIds) && this.localSkippedTokenIds.includes(String(tok.id)))
      );
    },
    getPatientTagText(tok) {
      if (!tok) return "Walk-in";
      if (this.isSkippedToken(tok)) return "Skipped";
      if (isEmergencyToken(tok) || tok.is_emergency || String(tok.booking_type || "").toLowerCase() === "emergency") return "Emergency";
      if (tok.booking_type) return tok.booking_type;
      const notes = String(tok.notes || tok.allergy || tok.description || "").trim();
      if (notes) {
        if (lower(notes).includes("allergy") || lower(notes).includes("penicillin")) {
          return notes.length > 20 ? notes.substring(0, 18) + "..." : notes;
        }
        if (lower(notes).includes("video")) return "Video visit";
        if (lower(notes).includes("prior")) return "Priority";
      }
      const sName = lower(tok.service?.name || tok.service_name || "");
      if (sName.includes("video") || sName.includes("tele")) return "Video visit";
      if (sName.includes("emergency") || sName.includes("urgent")) return "Priority";
      return "Walk-in";
    },
    getPatientTagBadgeClass(tok) {
      if (this.isSkippedToken(tok)) return "tag-skipped";
      const bType = String(tok.booking_type || "").toLowerCase();
      if (bType === "emergency" || isEmergencyToken(tok) || tok.is_emergency) return "tag-priority";
      if (bType === "vip") return "tag-allergy";
      if (bType === "appointment") return "tag-routine";
      if (bType === "online" || bType === "teleconsultation") return "tag-video";
      const tag = lower(this.getPatientTagText(tok));
      if (tag.includes("allergy")) return "tag-allergy";
      if (tag.includes("video")) return "tag-video";
      if (tag.includes("priority") || tag.includes("emerg")) return "tag-priority";
      return "tag-routine";
    },
    async completeServingToken() {
      if (!this.currentlyServingToken || this.isUpdatingQueue) return;
      const tok = this.currentlyServingToken;
      this.isUpdatingQueue = true;
      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        let success = false;
        try {
          const res = await axios.put(`${apiUrl}/v1/admin/tokens/complete/${tok.id}`, {}, { headers });
          if (res.data && res.data.success) success = true;
        } catch (e1) {
          console.warn("Direct complete failed:", e1.message);
        }

        if (!success) {
          await axios.put(`${apiUrl}/v1/admin/tokens/update/${tok.id}`, { status: "completed" }, { headers });
        }

        this.syncDoctorQueueState(tok, "Available", "--");
        Swal.fire({
          title: "Consultation Complete",
          text: `Token ${tok.token_number} marked completed.`,
          icon: "success",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
      } catch (err) {
        console.error("Error completing token:", err);
      } finally {
        this.isUpdatingQueue = false;
        this.loadDoctorSession(true);
      }
    },
    async setTokenCalling() {
      if (!this.currentlyServingToken || this.isUpdatingQueue) return;
      const tok = this.currentlyServingToken;
      this.isUpdatingQueue = true;
      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        await axios.put(`${apiUrl}/v1/admin/tokens/update/${tok.id}`, { status: "calling" }, { headers });
        tok.status = "calling";
        this.syncDoctorQueueState(tok, "Busy", tok.token_number);
      } catch (err) {
        console.error("Error setting calling:", err);
      } finally {
        this.isUpdatingQueue = false;
        this.loadDoctorSession(true);
      }
    },
    async setTokenServing() {
      if (!this.currentlyServingToken || this.isUpdatingQueue) return;
      const tok = this.currentlyServingToken;
      this.isUpdatingQueue = true;
      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        await axios.put(`${apiUrl}/v1/admin/tokens/update/${tok.id}`, { status: "serving" }, { headers });
        tok.status = "serving";
        this.syncDoctorQueueState(tok, "Busy", tok.token_number);
      } catch (err) {
        console.error("Error setting consulting:", err);
      } finally {
        this.isUpdatingQueue = false;
        this.loadDoctorSession(true);
      }
    },
    async moveTokenToWaiting() {
      if (!this.currentlyServingToken || this.isUpdatingQueue) return;
      const tok = this.currentlyServingToken;
      this.isUpdatingQueue = true;
      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        await axios.put(`${apiUrl}/v1/admin/tokens/update/${tok.id}`, { status: "waiting" }, { headers });
        tok.status = "waiting";
        this.syncDoctorQueueState(tok, "Available", "--");
      } catch (err) {
        console.error("Error moving to waiting:", err);
      } finally {
        this.isUpdatingQueue = false;
        this.loadDoctorSession(true);
      }
    },
    async skipServingToken() {
      if (!this.currentlyServingToken || this.isUpdatingQueue) return;
      const tok = this.currentlyServingToken;
      tok.status = "skipped";
      tok.is_skipped = true;
      if (!this.localSkippedTokenIds.includes(String(tok.id))) {
        this.localSkippedTokenIds = [...this.localSkippedTokenIds, String(tok.id)];
        try {
          localStorage.setItem("local_skipped_token_ids", JSON.stringify(this.localSkippedTokenIds));
        } catch (e) {
          // ignore
        }
      }
      this.isUpdatingQueue = true;
      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        try {
          await axios.put(`${apiUrl}/v1/admin/tokens/update/${tok.id}`, { status: "skipped", is_skipped: true }, { headers });
        } catch (e1) {
          await axios.put(`${apiUrl}/v1/admin/tokens/update/${tok.id}`, { status: "waiting", is_skipped: true }, { headers });
        }
        this.syncDoctorQueueState(tok, "Available", "--");
      } catch (err) {
        console.error("Error skipping token:", err);
      } finally {
        this.isUpdatingQueue = false;
        this.loadDoctorSession(true);
      }
    },
    syncDoctorQueueState(tok, status, tokenNumber = "--") {
      const me = this.doctors && this.doctors[0];
      if (me) {
        me.currentToken = tokenNumber;
        me.status = status;
      }
      localStorage.setItem("current_token", tokenNumber);
      localStorage.setItem("availability_status", status);

      const payload = {
        doctor_id: me ? me.id : tok?.user_id,
        user_id: me ? me.user_id || me.id : tok?.user_id,
        counter_id: me ? me.counter_id : tok?.counter_id,
        doctor_name: me ? me.name : tok?.token_attended,
        availability_status: status,
        status,
        currentToken: tokenNumber,
        current_token: tokenNumber,
      };
      window.dispatchEvent(new CustomEvent("doctor-status-synced", { detail: payload }));
      window.dispatchEvent(new CustomEvent("doctor-availability-changed", { detail: payload }));
    },
    async handleCallNextTrigger() {
      const emergencyTokens = this.visibleTokens.filter(
        (t) => isEmergencyToken(t) && ["pending", "waiting"].includes(lower(t.status))
      );
      const hasEmergency = emergencyTokens.length > 0;

      const result = await Swal.fire({
        title: "Call Patient",
        text: "How would you like to call the next patient?",
        icon: "question",
        showCancelButton: true,
        showDenyButton: hasEmergency,
        cancelButtonText: "Search & Pick",
        confirmButtonText: "Call Next in Queue",
        denyButtonText: `<i class="fas fa-ambulance me-1"></i> Call Emergency (${emergencyTokens.length})`,
        confirmButtonColor: "#1e3a5f",
        denyButtonColor: "#dc3545",
        cancelButtonColor: "#64748b",
      });

      if (result.isConfirmed) {
        this.callNextPatientInQueue(false);
      } else if (result.isDenied && hasEmergency) {
        this.callNextPatientInQueue(true);
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        this.handleManualSearchAndPick();
      }
    },
    async callNextPatientInQueue(emergencyOnly = false) {
      if (typeof window !== "undefined" && window.speechSynthesis && window.speechSynthesis.cancel) {
        window.speechSynthesis.cancel();
      }
      let targetToken = null;
      if (emergencyOnly) {
        const ems = this.visibleTokens.filter(
          (t) => isEmergencyToken(t) && ["pending", "waiting"].includes(lower(t.status))
        );
        if (ems.length === 0) {
          return Swal.fire("No Emergency Tokens", "No pending emergency tokens found in the queue.", "info");
        }
        ems.sort((a, b) => new Date(a.createdAt || a.created_at || 0) - new Date(b.createdAt || b.created_at || 0));
        targetToken = ems[0];
      } else {
        targetToken = this.nextWaitingToken;
        if (!targetToken) {
          return Swal.fire("Queue Empty", "No pending patients found in the queue.", "info");
        }
      }

      await this.assignOrCallToken(targetToken);
    },
    async assignOrCallToken(tok) {
      if (!tok || this.isCallingNext) return;
      this.isCallingNext = true;
      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        const me = this.doctors && this.doctors[0];
        const updatePayload = {
          status: "calling",
          token_attended: me ? me.name : getStoredName(),
          user_id: me ? me.id : localStorage.getItem("id"),
        };
        tok.status = "calling";
        if (me && me.room) updatePayload.counter_name = me.room;

        let assigned = false;
        try {
          const res = await axios.put(`${apiUrl}/v1/admin/tokens/assign/${tok.id}`, updatePayload, { headers });
          if (res.data && (res.data.success || res.data.data)) assigned = true;
        } catch (e1) {
          console.warn("Assign failed, falling back to update status:", e1.message);
        }

        if (!assigned) {
          await axios.put(`${apiUrl}/v1/admin/tokens/update/${tok.id}`, updatePayload, { headers });
        }

        // eslint-disable-next-line require-atomic-updates
        tok.status = "calling";
        this.syncDoctorQueueState(tok, "Busy", tok.token_number);

        if (this.socket) {
          try {
            this.socket.emit("recall_token", {
              token_id: tok.id,
              token_number: tok.token_number,
              token_name: tok.token_name,
              room: me?.room || "Consultation Room",
            });
          } catch (err) {
            console.warn("Socket notice:", err);
          }
        }

        this.playAnnouncementChime();
        this.speakAnnouncement(`Token number ${tok.token_number}, please proceed to room ${me?.room || 'one'}`);

        Swal.fire({
          title: tok.is_emergency ? `🚨 Emergency: ${tok.token_number}` : `Calling: ${tok.token_number}`,
          text: `Patient: ${tok.token_name || "Walk-in"}`,
          icon: tok.is_emergency ? "warning" : "success",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } catch (err) {
        console.error("Failed to call token:", err);
        Swal.fire("Error", err.response?.data?.message || "Failed to call token", "error");
      } finally {
        this.isCallingNext = false;
        this.loadDoctorSession(true);
      }
    },
    async handleManualSearchAndPick() {
      const availableTokens = this.visibleTokens.filter((t) =>
        ["pending", "waiting"].includes(lower(t.status))
      );

      if (availableTokens.length === 0) {
        return Swal.fire("No Available Tokens", "All pending patients have been called or queue is empty.", "info");
      }

      availableTokens.sort((a, b) => {
        const emA = isEmergencyToken(a) ? 1 : 0;
        const emB = isEmergencyToken(b) ? 1 : 0;
        if (emB !== emA) return emB - emA;
        return new Date(a.createdAt || a.created_at || 0).getTime() - new Date(b.createdAt || b.created_at || 0).getTime();
      });

      let tokenListItems = "";
      availableTokens.forEach((t) => {
        const isEm = isEmergencyToken(t);
        const isSk = this.isSkippedToken(t);
        const emergencyBadge = isEm ? `<span class="badge bg-danger ms-2 text-xxs"><i class="fas fa-ambulance me-1"></i>EMERGENCY</span>` : "";
        const skippedBadge = isSk ? `<span class="badge bg-danger-soft text-danger ms-2 text-xxs"><i class="fas fa-forward me-1"></i>SKIPPED</span>` : "";
        const timeStr = (t.createdAt || t.created_at) ? new Date(t.createdAt || t.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
        const ageStr = (t.age !== null && t.age !== undefined && t.age !== "") ? ` • Age: ${t.age}` : "";
        const phoneStr = t.phone_number ? ` • Tel: ${t.phone_number}` : "";
        const serviceStr = (t.service?.name || t.service_name) ? ` • ${t.service?.name || t.service_name}` : "";

        tokenListItems += `
          <div class="token-search-item p-2 d-flex justify-content-between align-items-center"
            data-id="${t.id}"
            data-search="${t.token_number} ${t.token_name || ''} ${t.phone_number || ''} ${t.service?.name || t.service_name || ''} ${isEm ? 'emergency' : ''} ${isSk ? 'skipped' : ''}"
            style="cursor:pointer; border-bottom:1px solid #f0f2f5; ${isEm ? 'background-color: #fff5f5;' : isSk ? 'background-color: #fff1f2;' : ''}">
            <div>
              <div class="d-flex align-items-center">
                <strong class="${isEm ? 'text-danger' : isSk ? 'text-danger' : 'text-dark'}">${t.token_number}</strong> - <span class="ms-1 fw-bold">${t.token_name || 'Walk-in'}</span> ${emergencyBadge} ${skippedBadge}
              </div>
              <small class="text-muted d-block text-xxs mt-1">
                <i class="far fa-clock me-1"></i>${timeStr}${serviceStr}${ageStr}${phoneStr}
              </small>
            </div>
            <div class="text-end">
              <span class="badge ${isEm ? 'bg-danger text-white' : 'bg-light text-primary'} mt-1">Select</span>
            </div>
          </div>
        `;
      });

      await Swal.fire({
        title: "Search & Pick Token",
        html: `
          <div class="text-start">
            <label class="form-label text-xs fw-bold text-uppercase text-secondary">Search Patient, Phone or Token Number</label>
            <div class="position-relative mb-3">
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0"><i class="fas fa-search text-xs text-secondary"></i></span>
                <input type="text" id="swal-manual-token-search" class="form-control border-start-0" placeholder="Type name, token number, phone, or 'emergency'..." autocomplete="off">
              </div>
              <div id="manual-token-dropdown" class="shadow-lg border rounded bg-white w-100 mt-1" style="display:block; max-height:240px; overflow-y:auto;">
                ${tokenListItems}
              </div>
            </div>
          </div>
        `,
        confirmButtonColor: "#1e3a5f",
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: "Cancel",
        didOpen: () => {
          const searchInput = document.getElementById("swal-manual-token-search");
          const dropdownList = document.getElementById("manual-token-dropdown");
          if (!searchInput || !dropdownList) return;
          const items = dropdownList.querySelectorAll(".token-search-item");

          searchInput.addEventListener("input", (e) => {
            const query = (e.target.value || "").toLowerCase().trim();
            items.forEach((item) => {
              const searchText = (item.getAttribute("data-search") || "").toLowerCase();
              item.style.display = searchText.includes(query) ? "flex" : "none";
            });
          });

          items.forEach((item) => {
            item.addEventListener("click", () => {
              const tokenId = item.getAttribute("data-id");
              const selectedToken = availableTokens.find((t) => String(t.id) === String(tokenId));
              Swal.close();
              if (selectedToken) {
                this.assignOrCallToken(selectedToken);
              }
            });
          });
        },
      });
    },
    async openCreateTokenModal() {
      let serviceOptions = `<option value="">Select a Service</option>`;
      (this.services || []).forEach((s) => {
        serviceOptions += `<option value="${s.id}">${s.name} ${s.prefix ? `(${s.prefix})` : ''}</option>`;
      });

      const { value: formValues } = await Swal.fire({
        title: "Create Walk-in Token",
        html: `
          <div class="text-start">
            <label class="form-label text-xs fw-bold text-secondary mb-1">Service</label>
            <select id="swal-select-service" class="form-select mb-2">${serviceOptions}</select>
            <label class="form-label text-xs fw-bold text-secondary mb-1">Patient Name</label>
            <input id="swal-input-name" class="form-control mb-2" placeholder="Full Name" />
            <div class="row g-2 mb-2">
              <div class="col-4">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Age</label>
                <input id="swal-input-age" type="number" class="form-control" placeholder="Age" />
              </div>
              <div class="col-4">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Gender</label>
                <select id="swal-select-gender" class="form-select">
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div class="col-4">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Phone</label>
                <input id="swal-input-phone" class="form-control" placeholder="Phone Number" />
              </div>
            </div>
            <label class="form-label text-xs fw-bold text-secondary mb-1">Notes / Reason</label>
            <textarea id="swal-input-notes" class="form-control" rows="2" placeholder="Symptoms or chief complaint..."></textarea>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonColor: "#0d9488",
        confirmButtonText: "Create Token",
        preConfirm: () => {
          return {
            name: document.getElementById("swal-input-name") ? document.getElementById("swal-input-name").value : '',
            phone: document.getElementById("swal-input-phone") ? document.getElementById("swal-input-phone").value : '',
            age: document.getElementById("swal-input-age") ? document.getElementById("swal-input-age").value : '',
            gender: document.getElementById("swal-select-gender") ? document.getElementById("swal-select-gender").value : '',
            notes: document.getElementById("swal-input-notes") ? document.getElementById("swal-input-notes").value : '',
            service_id: document.getElementById("swal-select-service") ? document.getElementById("swal-select-service").value : '',
          };
        },
      });

      if (formValues && formValues.name) {
        try {
          const token = getAuthToken();
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const base = getApiBase();
          const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

          const u = readStoredUser();
          const me = this.doctors && this.doctors[0];
          const payload = {
            token_name: formValues.name,
            phone: formValues.phone,
            phone_number: formValues.phone,
            age: formValues.age ? Number(formValues.age) : null,
            gender: formValues.gender || null,
            notes: formValues.notes,
            description: formValues.notes,
            service_id: formValues.service_id ? Number(formValues.service_id) : null,
            status: "pending",
            booking_type: "Walk-in",
            token_attended: (me && me.name) || getStoredName() || "Doctor",
            user_id: u.id || localStorage.getItem("id") || (me ? me.id : null),
            counter_id: localStorage.getItem("counter_id") || (me ? me.counter_id : null),
            tenant_id: this.currentTenantId,
          };
          try {
            await axios.post(`${apiUrl}/v1/admin/tokens/add`, payload, { headers });
          } catch (e) {
            await axios.post(`${apiUrl}/v1/admin/tokens`, payload, { headers });
          }
          localStorage.setItem("tokens_last_updated", String(Date.now()));
          window.dispatchEvent(new CustomEvent("token-created", { detail: payload }));
          this.loadDoctorSession(true);
        } catch (err) {
          console.error("Error creating token:", err);
        }
      }
    },
    broadcastEmergency(em) {
      this.activeEmergency = {
        title: `Emergency: ${em.token_number}`,
        description: `Patient ${em.token_name || 'Emergency alert'} requires immediate attention.`,
      };
    },
    dismissEmergency() {
      this.activeEmergency = null;
    },
    async openEmergencyPrompt() {
      const { value: reason } = await Swal.fire({
        title: "Emergency Broadcast",
        input: "text",
        inputLabel: "Alert message / Emergency reason",
        inputPlaceholder: "Code Blue / Trauma in Room 2...",
        showCancelButton: true,
        confirmButtonColor: "#dc2626",
      });

      if (reason) {
        this.activeEmergency = {
          title: "Code Emergency Alert",
          description: reason,
        };
      }
    },
    async loadDoctorSession(isBackground = false) {
      if (!isBackground) this.isFetchingSession = true;
      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        const safeGet = (paths) => {
          const list = Array.isArray(paths) ? paths : [paths];
          const tryNext = (idx) => {
            if (idx >= list.length) return Promise.resolve({ data: [] });
            return axios.get(`${apiUrl}/v1/admin/${list[idx]}`, { headers })
              .catch(() => tryNext(idx + 1));
          };
          return tryNext(0);
        };

        const [tokensTodayRes, tokensAllRes, countersRes, displaysRes, servicesRes] = await Promise.allSettled([
          safeGet(["tokens/list?status=today&view=board&all=true", "tokens/list?all=true"]),
          safeGet(["tokens/list?all=true", "tokens"]),
          safeGet(["counter", "counters"]),
          safeGet(["displays", "display"]),
          safeGet(["services/list", "services", "service"]),
        ]);

        let rawTokens = [];
        if (tokensTodayRes.status === "fulfilled" && tokensTodayRes.value) {
          rawTokens = extractItems(tokensTodayRes.value);
        }
        if ((!rawTokens || rawTokens.length === 0) && tokensAllRes.status === "fulfilled" && tokensAllRes.value) {
          rawTokens = extractItems(tokensAllRes.value);
        }

        const validTokens = Array.isArray(rawTokens) ? rawTokens.filter((t) => !this.isOtherTenant(t)) : [];
        this.todayTokens = validTokens;
        this.emergencyTokens = this.todayTokens.filter((t) => isEmergencyToken(t));

        if (countersRes.status === "fulfilled" && countersRes.value) {
          this.counters = extractItems(countersRes.value);
        }
        if (displaysRes.status === "fulfilled" && displaysRes.value) {
          this.displays = extractItems(displaysRes.value);
        }
        if (servicesRes.status === "fulfilled" && servicesRes.value) {
          this.services = extractItems(servicesRes.value);
        }

        // Auto-link doctor to counter if room matches counter name
        if (this.doctors[0] && Array.isArray(this.counters) && this.counters.length > 0) {
          const docRoom = lower(this.doctors[0].room || localStorage.getItem("doctorRoom") || "").replace(/^room\s*/i, "").trim();
          const docId = String(this.doctors[0].id || localStorage.getItem("id") || "");
          const matched = this.counters.find((c) => {
            const cName = lower(c.name || "").replace(/^room\s*/i, "").trim();
            const cUserId = String(c.user_id || (c.user ? c.user.id : ""));
            return (docRoom && cName === docRoom) || (docId && cUserId === docId);
          });
          if (matched) {
            this.doctors[0].counter_id = matched.id;
            this.doctors[0].room = matched.name;
            this.doctors[0].counter_name = matched.name;
            localStorage.setItem("counter_id", String(matched.id));
          }
        }
      } catch (err) {
        console.error("Error loading doctor session:", err);
      } finally {
        this.isFetchingSession = false;
      }
    },
  },
};
</script>

<style scoped>
/* Tokens */
.medical-dashboard {
  --md-bg: #f4f7fb;
  --md-surface: #ffffff;
  --md-line: #e8eef5;
  --md-teal: #0d9488;
  --md-text: #1e293b;
  --md-muted: #64748b;
  background: var(--md-bg);
  border-radius: 20px;
  min-height: 100%;
}

/* Colour helpers */
.text-teal { color: #0d9488 !important; }
.bg-teal { background-color: #0d9488 !important; }
.btn-teal { background-color: #0d9488 !important; border-color: #0d9488 !important; }
.bg-teal-soft { background-color: rgba(13, 148, 136, 0.12); }
.btn-outline-teal { border: 1px solid #0d9488 !important; color: #0d9488 !important; background: transparent; }
.btn-outline-teal:hover { background-color: #0d9488 !important; color: #ffffff !important; }
.bg-primary-soft { background-color: rgba(59, 130, 246, 0.12); }
.bg-warning-soft { background-color: rgba(245, 158, 11, 0.12); }
.bg-danger-soft { background-color: rgba(239, 68, 68, 0.12); }
.bg-success-soft { background-color: rgba(34, 197, 94, 0.12); }
.bg-secondary-soft { background-color: rgba(100, 116, 139, 0.15); }

/* Cards */
.md-card {
  background: var(--md-surface);
  border-radius: 16px;
  border: 1px solid var(--md-line);
  box-shadow: 0 2px 10px rgba(30, 41, 59, 0.04);
}

/* KPI */
.kpi-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tone-teal { background: #d9f5f1; color: #0d9488; }
.tone-blue { background: #dbeafe; color: #2563eb; }
.tone-amber { background: #fff1d6; color: #d97706; }
.tone-pink { background: #fde2ec; color: #db2777; }
.tone-rose { background: #ffe4e6; color: #e11d48; }
.kpi-label { font-size: 0.8rem; color: var(--md-muted); }
.kpi-value { font-weight: 800; color: var(--md-text); line-height: 1.1; }
.kpi-total { font-size: 0.9rem; font-weight: 500; color: var(--md-muted); }
.kpi-sub { font-size: 0.75rem; font-weight: 600; }

/* Doctor Hero Header Card */
.doctor-hero-card {
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.04);
  transition: all 0.25s ease;
}
.doctor-card-title {
  letter-spacing: -0.025em;
  font-size: 1.6rem;
  color: #0f172a;
}
.doctor-status-segmented-control {
  background: #f1f5f9;
  border-radius: 12px;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: 1px solid #e2e8f0;
}
.status-segment-btn {
  border: none;
  background: transparent;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 7px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  line-height: 1.2;
}
.status-segment-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.7);
  color: #0f172a;
}
.status-segment-btn.active.btn-segment-available {
  background: #0d9488 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(13, 148, 136, 0.35);
}
.status-segment-btn.active.btn-segment-busy {
  background: #e11d48 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(225, 29, 72, 0.35);
}
.status-segment-btn.active.btn-segment-break {
  background: #d97706 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(217, 119, 6, 0.35);
}
.status-segment-btn.active.btn-segment-offline {
  background: #475569 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(71, 85, 105, 0.35);
}
.emergency-header-btn {
  border: 1.5px solid #dc2626 !important;
  color: #dc2626 !important;
  background: #ffffff !important;
  border-radius: 9px !important;
  font-size: 0.85rem !important;
  font-weight: 700 !important;
  padding: 7px 18px !important;
  transition: all 0.2s ease;
}
.emergency-header-btn:hover {
  background: #fef2f2 !important;
  border-color: #b91c1c !important;
  color: #991b1b !important;
  transform: translateY(-1px);
}
.doctor-info-pill {
  background: #f1f5f9;
  color: #334155;
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}
.doctor-info-pill:hover {
  background: #e2e8f0;
  color: #0f172a;
  border-color: #cbd5e1;
}

/* Live Queue Styles */
.live-queue-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}
.live-waiting-badge {
  background-color: #d1fae5;
  color: #065f46;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
}
.now-serving-box {
  background-color: #f0fdf4;
  border: 1.5px solid #bbf7d0;
  border-radius: 14px;
}
.now-serving-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #0d9488;
  margin-bottom: 0.15rem;
}
.now-serving-token-number {
  font-size: 2.2rem;
  font-weight: 800;
  color: #065f46;
  line-height: 1.1;
  letter-spacing: -0.5px;
  margin-bottom: 0.35rem;
}
.now-serving-patient-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
}
.now-serving-subtext {
  font-size: 0.78rem;
  color: #64748b;
  margin-bottom: 0.5rem;
}
.now-serving-progress-track {
  width: 100%;
  height: 6px;
  background-color: #d1fae5;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}
.now-serving-progress-bar {
  height: 100%;
  background-color: #0d9488;
  border-radius: 999px;
  transition: width 0.3s ease;
}
.btn-serving-complete {
  background-color: #0f766e !important;
  border-color: #0f766e !important;
  color: #ffffff !important;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.45rem 1.1rem;
  border-radius: 8px;
  border: none;
}
.btn-serving-complete:hover {
  background-color: #0d6b63 !important;
}
.btn-serving-call {
  background-color: #ffffff !important;
  border: 1.5px solid #0f766e !important;
  color: #0f766e !important;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.4rem 0.95rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}
.btn-serving-call:hover:not(:disabled) {
  background-color: #f0fdf4 !important;
}
.btn-serving-call.btn-status-active {
  background-color: #0284c7 !important;
  border-color: #0284c7 !important;
  color: #ffffff !important;
}
.btn-serving-serve {
  background-color: #ffffff !important;
  border: 1.5px solid #059669 !important;
  color: #059669 !important;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.4rem 0.95rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}
.btn-serving-serve:hover:not(:disabled) {
  background-color: #ecfdf5 !important;
}
.btn-serving-serve.btn-status-active {
  background-color: #059669 !important;
  border-color: #059669 !important;
  color: #ffffff !important;
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.25);
}
.btn-serving-wait {
  background-color: #ffffff !important;
  border: 1.5px solid #d97706 !important;
  color: #d97706 !important;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.4rem 0.95rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}
.btn-serving-wait:hover:not(:disabled) {
  background-color: #fffbeb !important;
}
.btn-serving-skip {
  background-color: #ffffff !important;
  border: 1.5px solid #64748b !important;
  color: #64748b !important;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.4rem 0.95rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}
.btn-serving-skip:hover {
  background-color: #f1f5f9 !important;
}
.call-next-patient-btn {
  background-color: #1e3a5f !important;
  border-color: #1e3a5f !important;
  color: #ffffff !important;
  font-weight: 700;
  font-size: 0.88rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(30, 58, 95, 0.2);
  transition: all 0.2s ease;
}
.call-next-patient-btn:hover:not(:disabled) {
  background-color: #152943 !important;
  box-shadow: 0 6px 16px rgba(30, 58, 95, 0.3);
  transform: translateY(-1px);
}
.next-in-line-header {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1e293b;
}
.next-in-line-list {
  max-height: 380px;
  overflow-y: auto;
}
.next-patient-item {
  border-bottom: 1px solid #f1f5f9;
}
.next-patient-item:last-child {
  border-bottom: none;
}
.patient-token-badge {
  display: inline-block;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-weight: 700;
  font-size: 0.82rem;
  border-radius: 8px;
  padding: 0.3rem 0.65rem;
  min-width: 52px;
  text-align: center;
}
.patient-item-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e293b;
}
.patient-item-sub {
  font-size: 0.75rem;
  color: #64748b;
}
.patient-tag-badge {
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.25rem 0.65rem;
  white-space: nowrap;
}
.tag-allergy {
  background-color: #fef3c7 !important;
  color: #b45309 !important;
  border: 1px solid #fde68a;
}
.tag-video {
  background-color: #f1f5f9 !important;
  color: #475569 !important;
  border: 1px solid #e2e8f0;
}
.tag-priority {
  background-color: #fee2e2 !important;
  color: #dc2626 !important;
  border: 1px solid #fecaca;
}
.tag-routine {
  background-color: #e0f2fe !important;
  color: #0369a1 !important;
  border: 1px solid #bae6fd;
}
.tag-skipped {
  background-color: #ffe4e6 !important;
  color: #e11d48 !important;
  border: 1px solid #fecdd3 !important;
}
.item-skipped-highlight {
  background-color: #fff1f2 !important;
  border-left: 3px solid #f43f5e !important;
  border-radius: 8px;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
}
.token-skipped-badge {
  background: linear-gradient(135deg, #f43f5e 0%, #fb7185 100%) !important;
  color: #ffffff !important;
  border-color: #f43f5e !important;
}
.skipped-pill-badge {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background-color: #ffe4e6;
  color: #e11d48;
  display: inline-flex;
  align-items: center;
}
.emergency-list { max-height: 290px; overflow-y: auto; }
.emergency-btn { border-radius: 10px; }
.animate-pulse { animation: pulse-border 2s infinite; }
@keyframes pulse-border {
  0% { opacity: 1; }
  50% { opacity: 0.85; }
  100% { opacity: 1; }
}

/* ---------- Doctor Monthly Calendar Styles ---------- */
.doctor-calendar-card {
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #edf2f7;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
}
.doctor-calendar-header-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.2px;
}
.doctor-calendar-nav-btn {
  background: transparent;
  border: none;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
}
.doctor-calendar-nav-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
}
.doctor-calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
}
.doctor-calendar-weekday {
  font-size: 0.72rem;
  font-weight: 700;
  color: #94a3b8;
  padding: 2px 0;
}
.doctor-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
}
.doctor-calendar-day {
  position: relative;
  height: 38px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;
}
.doctor-calendar-day .day-num {
  line-height: 1;
  font-size: 0.82rem;
}
.calendar-token-count-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 15px;
  height: 15px;
  border-radius: 999px;
  background-color: #0d9488;
  color: #ffffff;
  font-size: 0.6rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(13, 148, 136, 0.35);
}
.calendar-token-count-badge.on-selected {
  background-color: #38bdf8;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
.doctor-calendar-day.has-tokens:not(.is-selected) {
  font-weight: 700;
  color: #0f172a;
  background-color: #f0fdfa;
}
.booked-token-badge {
  background: #f1f5f9;
  color: #0f172a;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 0.78rem;
  font-weight: 700;
  border: 1px solid #e2e8f0;
  min-width: 44px;
  text-align: center;
}
.doctor-booked-token-item {
  transition: all 0.15s ease;
}
.doctor-booked-token-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}
.doctor-calendar-day:hover:not(.is-selected) {
  background: #f1f5f9;
  color: #0f172a;
}
.doctor-calendar-day.is-other-month {
  color: #cbd5e1;
  font-weight: 400;
  opacity: 0.6;
}
.doctor-calendar-day.is-today:not(.is-selected) {
  border: 1.5px solid #0d9488;
  color: #0d9488;
  font-weight: 700;
}
.doctor-calendar-day.is-selected {
  background: #1e293b;
  color: #ffffff !important;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(30, 41, 59, 0.25);
}

/* Modal backdrop and card */
.doctor-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.doctor-modal-card {
  background: #ffffff;
  border-radius: 20px;
  width: 100%;
  max-width: 480px;
  overflow: hidden;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.2);
  border: 1px solid rgba(226, 232, 240, 0.8);
}
.doctor-slot-item {
  transition: all 0.15s ease;
}
.border-dashed {
  border-style: dashed !important;
}
.border-teal-soft {
  border-color: rgba(13, 148, 136, 0.25) !important;
}
.btn-xxs {
  font-size: 0.68rem !important;
  line-height: 1.2;
}
.text-xxs {
  font-size: 0.72rem !important;
}
.animate-scale-up {
  animation: scaleUpModal 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes scaleUpModal {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>
